import { useState, useEffect } from 'react';
import { getClubCatches } from '../../api/clubs';
import type { ClubCatchResponse } from '../../types/api';
import { CatchFeed } from '../catches/CatchFeed';
import { fromClubCatchResponse, type CatchRow } from '../catches/catchRow';

interface Props {
  clubId: number;
  manageMode?: boolean;
  onEdit?: (catchItem: ClubCatchResponse) => void;
  refreshKey?: number;
}

export function LatestCatchesList({ clubId, manageMode, onEdit, refreshKey }: Props) {
  const [catches, setCatches] = useState<ClubCatchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getClubCatches(clubId)
      .then(setCatches)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load catches'))
      .finally(() => setLoading(false));
  }, [clubId, refreshKey]);

  const renderAction = manageMode && onEdit
    ? (row: CatchRow) => {
        const original = catches.find(c => c.id === row.id);
        if (!original) return null;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(original); }}
            className="text-xs text-teal-600 hover:underline"
          >
            Edit
          </button>
        );
      }
    : undefined;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">Latest Catches</h2>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && catches.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No catches yet</p>
      )}

      {!loading && !error && catches.length > 0 && (
        <CatchFeed rows={catches.map(fromClubCatchResponse)} renderAction={renderAction} />
      )}
    </div>
  );
}
